<template>
  <div>
    <div v-if="!loading && !isDirectCostsEmpty" class="mb-3">
      <v-app>
        <div class="d-flex justify-end">
          <erase-icon-button
            v-if="removeButtonVisible"
            class="mr-2"
            @click="onDelete"
          />
          <business-guide-search v-model="searchValue" />
        </div>
      </v-app>
    </div>

    <ib-list-view-placeholder
      v-if="!loading && isDirectCostsEmpty"
      :title="$tc('addDirectCost')"
      :disabled="!canEdit || disabled"
      @click="onAddDirectCost"
    />
    <template v-else>
      <el-row
        v-if="!loading"
        :gutter="20"
        class="d-flex flex-wrap"
      >
        <el-col :md="12">
          <add-new-card
            :disabled="!canEdit"
            class="mb-2 py-md-0"
            :title="$tc('addDirectCost')"
            horizontal
            min-height="62px"
            @click="onAddDirectCost"
          />
        </el-col>

        <el-col
          v-for="directCost in filterDirectCosts"
          :key="directCost.id"
          class="d-flex mb-2"
          :md="12"
        >
          <div class="d-flex w-100">
            <set-up-and-projections-card
              class="w-100"
              :entity="directCost"
              :name="directCost.name"
              :date="`${$moment(directCost.starts).format('MMM YYYY')} - ${$moment(directCost.ends).format('MMM YYYY')}`"
              :amount="getFormattedAmount(directCost.average, 2)"
              :frequency="$t('pages.businessGuide.monthlyAverage')"
              type="direct-cost"
              :disabled="!canEdit"
              view="list"
              @edit="onEditDirectCost"
              @copy="onCopyDirectCost"
              @delete="onDeleteDirectCost"
            />
          </div>
        </el-col>
      </el-row>
    </template>

    <!-- Dialog Add Direct Cost -->
    <dialog-add-direct-cost
      v-if="canEdit"
      :dialog-add-direct-cost-visible="dialogAddNewVisible"
      :direct-cost-action="directCostAction"
      @close-dialog-add-direct-cost="dialogAddNewVisible = false"
      @close-dialog-on-button="dialogAddNewVisible = false; directCostAction = null"
      @on-update-direct-streams-view="onUpdateAddNewDialog()"
    />
    <!-- /Dialog Add Direct Cost -->
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import AddNewCard from '@/components/_v2/AddNewCard.vue'
import BusinessGuideSearch from '@/views/Home/Components/BusinessGuideSearch.vue'
import EraseIconButton from '@/components/_v2/EraseIconButton.vue'
import EventBus from '@/event-bus'
import MixinApiCalculations from '@/mixins/apiCalculations'
import MixinCurrencies from '@/mixins/currencies'
import MixinDeleteIdeaExamples from '@/mixins/deleteIdeaExamples'
import MixinIdeaRoles from '@/mixins/permissions/ideaRoles'
import SetUpAndProjectionsCard from '@/views/Home/StoryMode/Components/Cards/SetUpAndProjectionsCard.vue'

import DialogAddDirectCost from '@/views/Home/StoryMode/Forecast/DirectCosts/Dialogs/DialogAddDirectCost.vue'

export default {
  name: 'DirectCostsList',

  components: { EraseIconButton, DialogAddDirectCost, BusinessGuideSearch, AddNewCard, SetUpAndProjectionsCard },

  mixins: [MixinIdeaRoles, MixinCurrencies, MixinDeleteIdeaExamples, MixinApiCalculations],

  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      loading: false,
      searchValue: '',
      dialogAddNewVisible: false,
      directCostAction: null
    }
  },

  computed: {
    ...mapState(['idea']),

    ...mapGetters('idea', {
      directCosts: 'getDirectCosts'
    }),

    isDirectCostsEmpty () {
      return !this.directCosts.length
    },

    filterDirectCosts () {
      if (this.searchValue !== '' && this.searchValue) {
        return this.directCosts.filter(item => item.name.toUpperCase().includes(this.searchValue.toUpperCase()))
      }

      return this.directCosts
    },

    removeButtonVisible () {
      return this.canEdit && this.directCostHasItemsFromTemplate
    }
  },

  mounted () {
    EventBus.$on('add-edit', () => {
      this.onAddDirectCost()
    })

    EventBus.$on('remove-examples', () => {
      this.deleteExamples()
    })

    EventBus.$on('reload', () => {
      this.onUpdateAddNewDialog()
    })
  },

  beforeDestroy () {
    EventBus.$off('add-edit')
    EventBus.$off('remove-examples')
    EventBus.$off('reload')
  },

  methods: {
    ...mapActions('idea', [
      'deleteStoryModeDirectCost'
    ]),

    onAddDirectCost () {
      if (!this.canEdit) return
      this.dialogAddNewVisible = true
    },

    onEditDirectCost (directCost) {
      this.directCostAction = Object.assign({}, directCost)
      this.dialogAddNewVisible = true
    },

    onCopyDirectCost (directCost) {
      this.directCostAction = Object.assign({}, directCost)
      this.directCostAction.id = 0
      this.dialogAddNewVisible = true
    },

    onDeleteDirectCost (directCost) {
      this.emitLoading(true)
      this.deleteStoryModeDirectCost(directCost.id)
        .then(async () => {
          await this.getDirectCosts()
          await this.getTotalCosts()
          await this.getProfitAndCashFlow()
          this.emitLoading(false)
        })
    },

    onUpdateAddNewDialog () {
      this.emitLoading(true)
      Promise.all([
        this.getDirectCosts(),
        this.getTotalCosts(),
        this.getProfitAndCashFlow()
      ])
        .then(() => {
          this.emitLoading(false)
        })
        .finally(() => {
          this.emitLoading(false)
        })
    },

    deleteExamples () {
      this.deleteExamplesMixin('direct-costs')
        .then(_ => { this.onUpdateAddNewDialog() })
    },

    emitLoading (value) {
      this.loading = value
      EventBus.$emit('loading', value)
      this.$emit('loading', value)
    }
  }
}
</script>
